import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api, { formatApiError } from "@/api";
import { ParcelPhotosInput } from "@/components/ParcelPhotosInput";
import { useLanguage } from "@/context/LanguageContext";
import { toast } from "sonner";

const Field = ({ label, children, testid }) => (
  <label className="block" data-testid={testid}>
    <span className="text-xs font-semibold text-slate-500">{label}</span>
    {children}
  </label>
);

export const EditListingModal = ({ kind, item, open, onClose, onSaved }) => {
  const { t } = useLanguage();
  const isTrip = kind === "trip";
  const [form, setForm] = useState({ ...item });
  const [photos, setPhotos] = useState(item.photos || []);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = isTrip
        ? { date: form.date, time: form.time || "", seats: Number(form.seats), price: Number(form.price), accepts: form.accepts, vehicle: form.vehicle || "", notes: form.notes || "" }
        : { date: form.date, departure_date: form.departure_date || "", size: form.size, weight: Number(form.weight), description: form.description || "", price_offer: Number(form.price_offer), photos };
      await api.put(`/${isTrip ? "trips" : "parcels"}/${item.id}`, payload);
      toast.success(t(isTrip ? "dashboard.tripUpdated" : "dashboard.parcelUpdated"));
      onClose();
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err, t));
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "mt-1.5 h-11 rounded-xl";
  const selectCls = "mt-1.5 h-11 w-full rounded-xl border border-input bg-background px-3 text-sm";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-2xl max-h-[90vh] overflow-y-auto" data-testid="edit-listing-modal" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle data-testid="edit-listing-title">{t(isTrip ? "dashboard.editTripTitle" : "dashboard.editParcelTitle")}</DialogTitle>
        </DialogHeader>
        <p className="text-sm font-semibold text-primary" data-testid="edit-listing-route">{item.origin} → {item.destination}</p>
        <form onSubmit={save} className="space-y-3 mt-2">
          {isTrip ? (
            <>
              <div className="grid grid-cols-2 gap-3">
                <Field label={t("publish.date")} testid="edit-trip-date-field"><Input type="date" required data-testid="edit-trip-date" value={form.date || ""} onChange={set("date")} className={inputCls} /></Field>
                <Field label={t("publish.time")} testid="edit-trip-time-field"><Input type="time" data-testid="edit-trip-time" value={form.time || ""} onChange={set("time")} className={inputCls} /></Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label={t("publish.seats")} testid="edit-trip-seats-field"><Input type="number" min="1" max="8" required data-testid="edit-trip-seats" value={form.seats} onChange={set("seats")} className={inputCls} /></Field>
                <Field label={t("publish.price")} testid="edit-trip-price-field"><Input type="number" min="0" step="0.5" required data-testid="edit-trip-price" value={form.price} onChange={set("price")} className={inputCls} /></Field>
              </div>
              <Field label={t("publish.accepts")} testid="edit-trip-accepts-field">
                <select data-testid="edit-trip-accepts" value={form.accepts} onChange={set("accepts")} className={selectCls}>
                  <option value="passengers">{t("publish.passengers")}</option>
                  <option value="parcels">{t("publish.parcels")}</option>
                  <option value="both">{t("publish.both")}</option>
                </select>
              </Field>
              <Field label={t("publish.vehicle")} testid="edit-trip-vehicle-field"><Input data-testid="edit-trip-vehicle" value={form.vehicle || ""} onChange={set("vehicle")} className={inputCls} /></Field>
              <Field label={t("publish.notes")} testid="edit-trip-notes-field"><Input data-testid="edit-trip-notes" value={form.notes || ""} onChange={set("notes")} className={inputCls} /></Field>
            </>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3">
                <Field label={t("parcel.date")} testid="edit-parcel-date-field"><Input type="date" required data-testid="edit-parcel-date" value={form.date || ""} onChange={set("date")} className={inputCls} /></Field>
                <Field label={t("parcel.departureDate")} testid="edit-parcel-departure-field"><Input type="date" data-testid="edit-parcel-departure" value={form.departure_date || ""} onChange={set("departure_date")} className={inputCls} /></Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label={t("parcel.size")} testid="edit-parcel-size-field">
                  <select data-testid="edit-parcel-size" value={form.size} onChange={set("size")} className={selectCls}>
                    <option value="s">{t("parcel.sizeS")}</option>
                    <option value="m">{t("parcel.sizeM")}</option>
                    <option value="l">{t("parcel.sizeL")}</option>
                  </select>
                </Field>
                <Field label={t("parcel.weight")} testid="edit-parcel-weight-field"><Input type="number" min="0" step="0.1" required data-testid="edit-parcel-weight" value={form.weight} onChange={set("weight")} className={inputCls} /></Field>
              </div>
              <Field label={t("parcel.description")} testid="edit-parcel-description-field"><Input data-testid="edit-parcel-description" value={form.description || ""} onChange={set("description")} className={inputCls} /></Field>
              <Field label={t("parcel.priceOffer")} testid="edit-parcel-price-field"><Input type="number" min="0" step="0.5" required data-testid="edit-parcel-price" value={form.price_offer} onChange={set("price_offer")} className={inputCls} /></Field>
              <div data-testid="edit-parcel-photos-field">
                <span className="text-xs font-semibold text-slate-500">{t("parcel.photos")}</span>
                <div className="mt-1.5"><ParcelPhotosInput photos={photos} onChange={setPhotos} testid="edit-parcel-photos" /></div>
              </div>
            </>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" data-testid="edit-cancel-button" onClick={onClose} className="rounded-full">{t("dashboard.cancel")}</Button>
            <Button type="submit" disabled={saving} data-testid="edit-save-button" className="rounded-full bg-primary hover:bg-primary/90 text-white">{t("dashboard.saveChanges")}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
